import { useEffect, useRef, useState } from "react";
import { SkyButton, SkyCard, SkyMenu, SkySubtitle2 } from "@skypointcloud/skypoint-ui";
import type { SkyMenuContent } from "@skypointcloud/skypoint-ui";
import type { AccessibleInstance } from "../../../shared/types/rbac";
import { createSession } from "../../services/authSessionService";
import { authService } from "../../utils/authService";
import type { AccessDenialCode } from "./accessDenial";
import { goToInstance, tryAccessAgain } from "./accessNavigation";
import { ThemedLogo } from "../theme/ThemedLogo";

interface NoAccessScreenProps {
  code: AccessDenialCode;
  /** The instance refused, when known: tried again, and left out of Switch instance. */
  instanceId?: string | null;
  /** The instances the user can open, when the caller has them; otherwise asked for once. */
  instances?: AccessibleInstance[];
}

/** The codes whose way out can be another instance. */
const SWITCHABLE: ReadonlySet<AccessDenialCode> = new Set([
  "NO_ASSIGNMENT",
  "INSTANCE_NOT_FOUND",
  "NO_PRODUCT_ACCESS",
]);

/** The codes a later change can fix: a grant, a restored instance, a check that works. */
const RETRYABLE: ReadonlySet<AccessDenialCode> = new Set([
  "NO_ASSIGNMENT",
  "INSTANCE_NOT_FOUND",
  "NO_PRODUCT_ACCESS",
  "CHECK_FAILED",
]);

function messageFor(code: AccessDenialCode): string {
  switch (code) {
    case "NO_ASSIGNMENT":
      // skySupport's own words for this, so the two apps say the same thing.
      return "You don't have access to this application yet. Please contact your administrator to request access and be assigned the appropriate role.";
    case "INSTANCE_NOT_FOUND":
      return "This instance doesn't exist or was removed.";
    case "NO_PRODUCT_ACCESS":
      // HomeRoute's answer for a signed-in user holding no role in any product here. Names no
      // product, so it says nothing about what the instance has.
      return "You don't have a role in any product this workspace includes. Switch to another instance, or ask your administrator for access.";
    case "NO_ACCOUNT":
      return "We couldn't find your skyHire account. Sign out and sign in again, or contact your admin.";
    case "ACCOUNT_BLOCKED":
      return "Your account is blocked. Contact your admin.";
    case "CHECK_FAILED":
      return "We couldn't check your access.";
  }
}

/**
 * What a signed-in user sees when the server won't let them use skyHire (Phase U,
 * deny-by-default). An end state: it stands in for the whole app shell, so no
 * header, sidebar or page is mounted behind it asking for data, and nothing sends
 * the user to sign-in by itself (that loops through B2C). Every way out is the
 * user's choice and a full page load.
 */
export function NoAccessScreen({ code, instanceId, instances }: NoAccessScreenProps) {
  const switchable = SWITCHABLE.has(code);
  const [fetched, setFetched] = useState<AccessibleInstance[] | null>(null);
  const asked = useRef(false);

  // Asked once, only when Switch instance could be offered and the caller had no list.
  useEffect(() => {
    if (!switchable || instances || asked.current) return;
    asked.current = true;
    createSession().then(
      (session) => setFetched(session.accessibleInstances),
      () => setFetched([]),
    );
  }, [switchable, instances]);

  const others = switchable
    ? (instances ?? fetched ?? []).filter((instance) => instance.instanceId !== instanceId)
    : [];
  const menuItems: SkyMenuContent[] = others.map((instance) => ({
    key: instance.instanceId,
    label: instance.instanceName || instance.instanceId,
    onClick: () => goToInstance(instance.instanceId),
  }));

  const signOut = () => {
    void authService.logout();
  };
  const canSwitch = menuItems.length > 0;
  const canRetry = RETRYABLE.has(code);

  return (
    <main className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-muted px-4">
      <SkyCard className="w-full max-w-md p-6 flex flex-col items-center gap-4 text-center">
        {/* The dark-text wordmark on a light card, the white-text one in dark mode. */}
        <ThemedLogo
          className="h-9"
          src="/dist/img/skyHire_Dark.svg"
          darkSrc="/dist/img/skyHire_Light.svg"
          alt="skyHire"
        />
        <SkySubtitle2 className="block m-0 text-center">{messageFor(code)}</SkySubtitle2>
        <div className="flex flex-wrap justify-center gap-2">
          {canSwitch && (
            <SkyMenu
              trigger={<SkyButton appearance="primary">Switch instance</SkyButton>}
              items={menuItems}
            />
          )}
          {canRetry && (
            <SkyButton
              appearance={canSwitch ? "outline" : "primary"}
              onClick={() => tryAccessAgain(code, instanceId)}
            >
              Try again
            </SkyButton>
          )}
          <SkyButton
            appearance={canSwitch || canRetry ? "outline" : "primary"}
            onClick={signOut}
          >
            Sign out
          </SkyButton>
        </div>
      </SkyCard>
    </main>
  );
}
